import initApollo from './InitApollo'
import Head from 'next/head'
import { getDataFromTree } from '@apollo/client/react/ssr'
import { captureException } from 'utils/Sentry'
import { Component } from 'react'

const WithApolloClient = (App: any) => {
  return class Apollo extends Component<any, any> {
    apolloClient

    static displayName = 'withApollo(App)'
    static async getInitialProps(ctx) {
      const {
        ctx: { req, AppTree },
      } = ctx
      let appProps = {}
      if (App.getInitialProps) {
        try {
          appProps = await App.getInitialProps(ctx)
        } catch (err) {
          captureException(err, ctx?.ctx)
          appProps = {
            pageProps: {
              errorCode: 500,
            },
          }
        }
      }

      // Run all GraphQL queries in the component tree
      // and extract the resulting data
      const { pageProps } = appProps as any
      const cookies = req?.headers?.cookie
      const apolloClient = initApollo({
        domain: pageProps?.domain,
        host: pageProps?.host,
        cookies,
        cloudflareForwardedIp: pageProps?.cloudflareForwardedIp,
      })
      const shouldBuildCache = !pageProps?.isServerSideRedirect && !process.browser
      if (shouldBuildCache) {
        try {
          // Run all GraphQL queries
          await getDataFromTree(<AppTree {...appProps} apolloClient={apolloClient} />)
        } catch (error) {
          // Prevent Apollo Client GraphQL errors from crashing SSR.
          // Handle them in components via the data.error prop:
          // https://www.apollographql.com/docs/react/api/react-apollo.html#graphql-query-data-error
          console.error('Error while running `getDataFromTree`', error)
        }

        // getDataFromTree does not call componentWillUnmount
        // head side effect therefore need to be cleared manually
        Head.rewind()
      }

      // Extract query data from the Apollo store
      const apolloState = apolloClient.cache.extract()
      return {
        ...appProps,
        apolloState,
      }
    }

    constructor(props) {
      super(props)
      const { pageProps, apolloClient } = props
      if (apolloClient) {
        this.apolloClient = apolloClient
      } else {
        this.apolloClient = initApollo({
          initialState: props?.apolloState,
          domain: pageProps?.domain,
          host: pageProps?.host,
          cloudflareForwardedIp: pageProps?.cloudflareForwardedIp,
        })
      }
    }

    render() {
      return <App {...this.props} apolloClient={this.apolloClient} />
    }
  }
}

export default WithApolloClient
